import { Date_Format, deepClone } from '@core-web/lib';
import { getSessionCookie } from './storage';
import { ICurrencyConversion } from '@echotex/api-merchandising';
import dayjs from 'dayjs';
import { IItemNameSequence } from '@echotex/api-scm';
import { get } from 'react-hook-form';

export const duplicateArray = (originalArray: any[], newLength: number) => {
  const originalLength = originalArray.length;

  if (newLength % originalLength !== 0) {
    throw new Error('New array length must be a multiple of the original array length.');
  }

  const factor = newLength / originalLength;
  const duplicatedArray = Array.from({ length: factor }, () => [...originalArray]).flat();

  return duplicatedArray;
};

const handleScroll = (e: any) => {
  e.stopPropagation();
  const target = e.target;
  const isAtTop = target.scrollTop === 0;
  const isAtBottom = target.scrollTop + target.clientHeight === target.scrollHeight;
  if (isAtTop) {
    !target?.classList.contains('isAtTop') && target?.classList.add('isAtTop');
  } else {
    target?.classList.contains('isAtTop') && target?.classList.remove('isAtTop');
  }

  if (isAtBottom) {
    !target?.classList.contains('isAtBottom') && target?.classList.add('isAtBottom');
  } else {
    target?.classList.contains('isAtBottom') && target?.classList.remove('isAtBottom');
  }
};

export const addScrollbarClasses = (selector: string) => {
  const elementPoller = setInterval(() => {
    const element = document.querySelector(selector);
    if (element) {
      clearInterval(elementPoller);
      element?.removeEventListener('scroll', handleScroll);
      element?.addEventListener('scroll', handleScroll);
    }
  }, 100);
};

export const errorChecker = (errors: any, name: string) => {
  return get(errors, name) ?? null;
};

export const escapeRegExp = (keyword: string): string => {
  return keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escapes special characters
};

export const getCookieValueToBoolean = (key: string) => {
  const cookieValue = getSessionCookie(key);
  const hasAnyValue = cookieValue === 'true' ? true : false;
  return hasAnyValue;
};

export const generateUniqueString = (length: number = 6): string => {
  // Strictly enforce that length is a positive integer and at least 2
  if (!Number.isInteger(length) || length < 2) {
    throw new Error('Length must be an integer and at least 2 characters.');
  }

  // Generate a random letter from a-z
  const alphabet = 'abcdefghijklmnopqrstuvwxyz';
  const randomLetter = alphabet[Math.floor(Math.random() * alphabet.length)];

  // Generate the rest of the string, ensuring uniqueness
  const randomPart = Math.random().toString(36).slice(2) + Date.now().toString(36);
  const combinedString = randomLetter + randomPart;

  // Return the substring of the desired length
  return combinedString.slice(0, length);
};

export const getEntityWithLowerPriority = (arr: IItemNameSequence[]) => {
  // Filter out the entities with values 2 and 3
  const filtered = arr.filter((item) => item.entity === 2 || item.entity === 3);

  // Compare the priorities and return the entity with the lower priority value
  if (filtered.length === 2) {
    return filtered[0].priority < filtered[1].priority ? filtered[0].entity : filtered[1].entity;
  }

  // Return null if entities 2 or 3 are not found
  return null;
};

export const getChunkArrayFromArray = (sourceArray: any[], chunkSize: number): any[][] => {
  if (chunkSize <= 0) {
    throw new Error('Chunk size must be greater than 0.');
  }

  const result: any[][] = [];
  for (let i = 0; i < sourceArray.length; i += chunkSize) {
    result.push(sourceArray.slice(i, i + chunkSize));
  }
  return result;
};

// Will be used for multiple dependencyValues extracting inside the geQueryParams method
export const getValueBySuffixFromObject = (obj: Record<string, any>, suffix: string): any | undefined => {
  // Find the full key in the object that ends with the given suffix
  const fullKey = Object.keys(obj).find((key) => key.endsWith(suffix));

  // Return the value if the key exists, otherwise return undefined
  return fullKey ? obj[fullKey] : undefined;
};

export const getCacheFieldName = (name: string): string => {
  return name + '-cache';
};

export const getFileFieldName = (name: string): string => {
  return name + '_file';
};

/**
 * The method to extract the number from a string
 * @param input takes a string as input
 * @returns returns the number of the size
 */
export const extractNumberInParentheses = (input: string): string | null => {
  const match = input?.match(/\((\d+)\)/);
  return match ? match[1] : null;
};

/**
 * The method to extract last content of parantheses from a string
 * @param input takes a string as input
 * @returns returns the number of the size
 */
export const extractLastParenthesesContent = (str: string = '') => {
  const matches = [...str.matchAll(/\(([^)]+)\)/g)];
  return matches.length > 0 ? matches[matches.length - 1][1] : null;
};

// Can be used to extract index number from a string
// example for "style[0]" it will return 0, for "style[1].data[5].name" it will return 1
export const getIndexNumberFromFieldName = (str: string): number => {
  const match = str.match(/\[(\d+)\]/);
  return match ? parseInt(match[1], 10) : -1;
};

export const getLastIndexNumberFromFieldName = (str: string): number => {
  const matches = [...str.matchAll(/\[(\d+)\]/g)];
  return matches.length ? Number(matches[matches.length - 1][1]) : -1;
};

// Can be used to extract indexed rowTitle of Grid Form
// example for "style[0].fabrics" it will return "style[0]", for "style[1].data[5].name" it will return "style[1]"
export const getRowTitleWithIndexOnly = (str: string): string => {
  const match = str.match(/^[^\.\[]*\[\d+\]/);
  return match ? match[0] : '';
};

// Can be used to extract actual fieldTitle from a indexed fieldName
// example for "style[0].fabrics" it will return "fabrics", for "style[1].fabrics[5].yarn" it will return "yarn"
export const getFieldTitleFromGridIndexedName = (str: string): string => {
  const match = str.match(/\.(\w+)$/);
  return match ? match[1] : '';
};

// Can be used to extract the parent fieldTitle before the last index
// example for "style[0].fabrics" it will return "style",
// for "style[1].fabrics[5].yarn" it will return "style[1].fabrics"
export const getParentFieldTitleBeforeLastIndex = (str: string): string => {
  const match = str.match(/^(.*?)(?=\[\d+\][^\[]*$)/);
  return match ? match[1] : '';
};

// Can be used to extract actual row title for multi row of grid form
// example for "style[0].styleWiseFabrics[0].fabricWiseYarn[0].processLossPercentage" it will return "style[0].styleWiseFabrics[0].fabricWiseYarn[0]"
export const getIndexedMultiRowTitle = (name: string) => {
  const segments = name.split('.');
  segments.pop(); // Remove the last segment
  return segments.join('.');
};

// Used to calculate actual quantity from base quantity and processLoss percentage
export const calculateActualQuantity = (base: string | number, percentage: string | number, isExcess?: boolean) => {
  const baseNum = Number(base);
  const percentageNum = Number(percentage);

  if (!isNaN(baseNum) && !isNaN(percentageNum)) {
    const percentageValue = baseNum * (percentageNum / 100);
    const totalQuantity = isExcess ? baseNum + percentageValue : baseNum - percentageValue;
    return Math.round(totalQuantity);
  }

  return 'Invalid input: both parameters must be numbers or numeric strings';
};

export const calculatePercentageValue = (base: string | number, percentage: string | number, decimalPlaces = 2) => {
  // Convert inputs to numbers
  const baseNum = Number(base);
  const percentageNum = Number(percentage);

  // Validation checks
  if (isNaN(baseNum) || isNaN(percentageNum)) {
    return 'Invalid input: Both parameters must be numbers or numeric strings';
  }

  if (baseNum <= 0) {
    return 'Invalid input: Base quantity must be greater than zero';
  }

  if (percentageNum < 0) {
    return 'Invalid input: Percentage cannot be negative';
  }

  // Calculate the percentage value
  const percentageValue = (baseNum * percentageNum) / 100;

  return parseFloat(percentageValue.toFixed(decimalPlaces));
};

export const calculateTotalValue = (quantity: string | number, unitValue: string | number, decimalPlaces = 2) => {
  // Convert inputs to numbers
  const baseQty = Number(quantity);
  const baseUnitValue = Number(unitValue);

  // Validation checks
  if (isNaN(baseQty) || isNaN(baseUnitValue)) {
    return '';
  }

  // Calculate the totalValue
  let totalValue: number | string = '';
  if (baseQty && baseUnitValue) {
    totalValue = baseQty * baseUnitValue;
  } else {
    return totalValue;
  }

  return parseFloat(totalValue.toFixed(decimalPlaces));
};

export const extractFilenameWithDynamicExtension = (path: string) => {
  // Validate the input
  if (typeof path !== 'string') {
    throw new Error('Invalid input. Path must be a string.');
  }

  // Find the indices of the last "/" and last "_"
  const lastSlashIndex = path.lastIndexOf('/');
  const lastUnderscoreIndex = path.lastIndexOf('.');

  if (lastSlashIndex === -1 || lastUnderscoreIndex === -1 || lastUnderscoreIndex < lastSlashIndex) {
    return null; // Return null if the path format is invalid
  }

  // Extract the filename between "/" and "_"
  const filenameBase = path.substring(lastSlashIndex + 1, path.lastIndexOf('_'));

  // Extract the extension
  const extension = path.substring(lastUnderscoreIndex);

  // Return the complete filename with its original extension
  return `${filenameBase}${extension}`;
};

// Extend array with new item of total row
export const extendArrayWithTotalRow = (items: any[], sumKeys: string[], fixedValues: Record<string, any>): any[] => {
  const totalRow: any = {};

  // Initialize total row with the same structure as other items
  if (items.length > 0) {
    Object.keys(items[0]).forEach((key) => {
      totalRow[key] = sumKeys.includes(key) ? 0 : null; // Initialize sum keys to 0, others to null
    });
  }

  // Sum up values for keys in `sumKeys`
  sumKeys.forEach((key) => {
    totalRow[key] =
      fixedValues[key] !== undefined
        ? fixedValues[key] // Use fixed value if specified
        : items.reduce((sum, item) => {
            const value = parseFloat(item[key]?.label || item[key]) || 0; // Parse value safely
            return sum + value;
          }, 0);
  });

  // Assign fixed values for other keys in `fixedValues`
  Object.keys(fixedValues).forEach((key) => {
    totalRow[key] = fixedValues[key];
  });

  return [...items, totalRow];
};

export const areObjectsDeepEqual = (
  obj1: any,
  obj2: any,
  keysToCheck?: string[],
  hasKeys?: boolean,
  keysToIgnore?: string[]
) => {
  if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
    return false; // Both must be objects and not null
  }

  const keys =
    keysToCheck && keysToCheck.length
      ? keysToCheck.filter((key) => !(keysToIgnore || []).includes(key)) // Exclude keysToIgnore
      : Object.keys(obj1).filter((key) => !(keysToIgnore || []).includes(key)); // Default to all keys in obj1, excluding keysToIgnore

  // If hasKeys flag is true, first check if obj2 has the same keys as obj1
  if (hasKeys) {
    for (const key of keys) {
      if (!Object.prototype.hasOwnProperty.call(obj1, key) || !Object.prototype.hasOwnProperty.call(obj2, key)) {
        return false; // Key must exist in both objects
      }
    }
  }

  // Now perform deep equality check
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(obj1, key) || !Object.prototype.hasOwnProperty.call(obj2, key)) {
      return false; // Key must exist in both objects
    }
    if (obj1[key] === obj2[key]) continue; // Handles primitives and reference equality
    if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object' && obj1[key] !== null && obj2[key] !== null) {
      if (!areObjectsDeepEqual(obj1[key], obj2[key], undefined, undefined, keysToIgnore)) {
        return false; // Recursively check nested keys/values
      }
    } else {
      return false; // If primitive values don't match
    }
  }

  return true;
};

// Interface for the DirtyChangesDetectionFields
interface IMarkDirtyChangesInArrayConfig {
  uniqueKeys: Record<string, any>;
  urlKey: string;
  dateKeys: string[];
  withDeleted?: boolean;
}
export const defaultMarkDirtyFieldsConfig: IMarkDirtyChangesInArrayConfig = {
  uniqueKeys: {},
  urlKey: '',
  dateKeys: [],
};

// Helper function to determine if a value is an object
export const isObject = (value: any): boolean => value && typeof value === 'object' && !Array.isArray(value);

export const markDirtyChangesInArray = (
  sourceArray: any[],
  prevValuesArray: any[],
  dirtyCheckConfig: IMarkDirtyChangesInArrayConfig
): any[] => {
  if (!prevValuesArray?.length) return sourceArray;
  const { uniqueKeys, urlKey, dateKeys, withDeleted } = dirtyCheckConfig;
  const sourceData = deepClone(sourceArray);

  const formatDate = (value: any) =>
    isObject(value) ? value?.format('DD-MM-YYYY') : value ? dayjs(value).format('DD-MM-YYYY') : value;

  const transformNestedValues = (data: any, isNew: boolean = false, isDeleted: boolean = false): any => {
    // Helper function to check if an object has both 'label' and 'value' keys
    const hasLabelAndValue = (obj: any): boolean => obj && typeof obj === 'object' && 'label' in obj && 'value' in obj;

    // Recursive function to transform the data
    const transform = (value: any): any => {
      if (Array.isArray(value)) {
        return value.map((item) => transform(item));
      } else if (typeof value === 'object' && value !== null) {
        if (hasLabelAndValue(value)) {
          return { ...value, isNew, isDeleted }; // Add `isNew` & `isDeleted` flags
        } else {
          // Recursively transform nested objects
          return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, transform(val)]));
        }
      } else {
        // Convert primitive values to object format
        return { label: value, isNew, isDeleted };
      }
    };

    return transform(data);
  };

  // Helper function to set the modified value with isDirty, isNew, and prevValue
  const setDirtyValue = (sourceValue: any, prevValue: any): any => {
    const isNullOrEmpty = (value: any): boolean => {
      return value === null || value === undefined || value === '';
    };
    if (isNullOrEmpty(sourceValue) && isNullOrEmpty(prevValue)) {
      return {
        label: sourceValue,
        value: sourceValue,
        prevValue: prevValue,
      };
    }
    if (isObject(sourceValue)) {
      return { ...sourceValue, isDirty: true, prevValue: prevValue?.label || prevValue };
    } else {
      return {
        label: sourceValue,
        value: sourceValue,
        isDirty: true,
        prevValue: prevValue,
      };
    }
  };

  // Recursive function to update the source object based on differences with previous values
  const updateObject = (source: any, prevValues: any): any => {
    if (!isObject(source)) {
      return source;
    }

    const updatedObject = { ...source };

    Object.keys({ ...source, ...prevValues }).forEach((key) => {
      const sourceValue = source?.[key];
      const prevValue = prevValues?.[key];

      if (dateKeys.includes(key)) {
        const sourceDateValue = formatDate(sourceValue);
        const prevDateValue = formatDate(prevValue);

        if (sourceDateValue !== prevDateValue) {
          updatedObject[key] = setDirtyValue(sourceDateValue, prevDateValue);
        } else {
          updatedObject[key] = sourceDateValue;
        }
      } else if (Array.isArray(sourceValue) && Array.isArray(prevValue)) {
        const uniqueKey = uniqueKeys?.[key];

        if (key === urlKey && sourceValue?.length === 1) {
          return setDirtyValue(sourceValue[0], prevValue[0]);
        }

        // Recursively update each item in the array
        updatedObject[key] = sourceValue.map((item: any, index: number) => {
          const currentUniqueItem = item[uniqueKey];
          const isUrl = uniqueKey === urlKey;
          const isUniqueKeyValueObject =
            typeof currentUniqueItem === 'object' &&
            currentUniqueItem?.hasOwnProperty('label') &&
            currentUniqueItem?.hasOwnProperty('value');

          if (!currentUniqueItem && !isUrl && !uniqueKey) {
            return updateObject(item, prevValue[index]);
          }

          const prevItem = prevValue.find((prev: any) => {
            if (isUrl) {
              return prev[uniqueKey]?.[0] === currentUniqueItem?.[0];
            } else if (isUniqueKeyValueObject) {
              return prev[uniqueKey]?.label === currentUniqueItem?.label;
            } else {
              return prev[uniqueKey] === currentUniqueItem;
            }
          });

          if (!prevItem) {
            return transformNestedValues(item, true);
          } else {
            return updateObject(item, prevItem);
          }
        });

        // Handle extra items in prevValues that are not in sourceValue
        if (withDeleted) {
          const deletedItems = prevValue.filter((item: any) => {
            const currentUniqueItem = item[uniqueKey];
            const isUrl = uniqueKey === urlKey;
            const isUniqueKeyValueObject =
              typeof currentUniqueItem === 'object' &&
              currentUniqueItem?.hasOwnProperty('label') &&
              currentUniqueItem?.hasOwnProperty('value');

            return !sourceValue.find((sourceItem: any) => {
              if (isUrl) {
                return sourceItem[uniqueKey]?.[0] === currentUniqueItem?.[0];
              } else if (isUniqueKeyValueObject) {
                return sourceItem[uniqueKey]?.label === currentUniqueItem?.label;
              } else {
                return sourceItem[uniqueKey] === currentUniqueItem;
              }
            });
          });
          if (deletedItems?.length) {
            updatedObject[key] = [
              ...(updatedObject[key] || []),
              ...deletedItems.map((item: any) => transformNestedValues(item, false, true)),
            ];
          }
        }
      } else if (isObject(sourceValue) && isObject(prevValue)) {
        // Recursively update nested objects
        if (sourceValue?.label && sourceValue?.value && sourceValue?.label !== prevValue?.label) {
          // Set dirty value if source and previous labels differ
          updatedObject[key] = setDirtyValue(sourceValue, prevValue);
        } else {
          updatedObject[key] = updateObject(sourceValue, prevValue);
        }
      } else if (sourceValue !== prevValue) {
        // Set dirty value if source and previous values differ
        updatedObject[key] = setDirtyValue(sourceValue, prevValue);
      }
    });

    return updatedObject;
  };

  // Iterate through the source array and modify it based on differences with previous values
  return sourceData?.map((item: any, index: number) => {
    return updateObject(item, prevValuesArray[index] || []);
  });
};

/**
 * The method to convert any string to camel case format
 * @param str receives a string
 * @returns returns the converted camel case format
 */
export const toCamelCase = (str: string = ''): string => {
  return str.charAt(0).toLowerCase() + str.slice(1);
};

/**
 * The method to scroll into an element view
 * @param element the element where the scroll should be performed
 */
export const scrollIntoView = (element: HTMLElement): void => {
  element?.scrollIntoView({
    behavior: 'smooth',
  });
};

export const getDropdownOptionsOfNumberBetweenRange = (finalNumber: number, initialNumber: number = 1) => {
  const options = [];
  for (let i = initialNumber; i <= finalNumber; i++) {
    options.push({ label: `${i}`, value: i });
  }
  return options;
};

/**
 * The method to remove duplicates from an array of object
 * @param str receives a string
 * @returns returns the converted camel case format
 */
export const makeUniqueArray = (arr: any[] = []): any[] => {
  return Array.from(
    new Set(arr.map((item) => JSON.stringify(item))) // Set of strings
  ).map((str) => JSON.parse(str));
};

export const convertCurrency = (
  convertFromCurrency: string,
  convertToCurrency: string,
  value: number | string,
  currencyData: ICurrencyConversion[] | null,
  decimalPoints: number = 2
): number | '' => {
  if (
    !convertFromCurrency ||
    !convertToCurrency ||
    value === undefined ||
    value === null ||
    isNaN(Number(value)) ||
    !Array.isArray(currencyData)
  ) {
    return '';
  }

  const numericValue = Number(value);

  if (convertFromCurrency === convertToCurrency) {
    return Number(numericValue.toFixed(decimalPoints));
  }

  const conversion = currencyData.find(
    (entry) =>
      (entry.baseCurrencyShortCode.toLowerCase() === convertFromCurrency.toLowerCase() &&
        entry.conversionCurrencyShortCode.toLowerCase() === convertToCurrency.toLowerCase()) ||
      (entry.baseCurrencyShortCode.toLowerCase() === convertToCurrency.toLowerCase() &&
        entry.conversionCurrencyShortCode.toLowerCase() === convertFromCurrency.toLowerCase())
  );

  if (!conversion) return '';

  const { baseCurrencyShortCode, conversionCurrencyShortCode, conversionRate, baseFactor } = conversion;

  let result: number;

  if (convertFromCurrency === baseCurrencyShortCode && convertToCurrency === conversionCurrencyShortCode) {
    result = (numericValue * conversionRate) / baseFactor;
  } else if (convertFromCurrency === conversionCurrencyShortCode && convertToCurrency === baseCurrencyShortCode) {
    result = (numericValue / conversionRate) * baseFactor;
  } else {
    return '';
  }

  return Number(result.toFixed(decimalPoints));
};

export const convertTotalValue = (
  convertFromCurrency: string,
  value: number | string,
  currencyData: ICurrencyConversion[] | null,
  decimalPoints: number = 2
) => {
  if (
    !convertFromCurrency ||
    value === undefined ||
    value === null ||
    isNaN(Number(value)) ||
    !Array.isArray(currencyData)
  ) {
    return '';
  }
  const conversion = currencyData.find(
    (entry) => entry.conversionCurrencyShortCode.toLowerCase() === convertFromCurrency.toLowerCase()
  );

  const { conversionCurrencyShortCode = '', conversionRate = 0, baseFactor = 0 } = conversion || {};
  const numericValue = Number(value);
  let result: number;

  if (convertFromCurrency === conversionCurrencyShortCode) {
    result = (numericValue / conversionRate) * baseFactor;
  } else {
    return numericValue || '';
  }

  return Number(result.toFixed(decimalPoints));
};

export const formatDecimal = (value: any, space?: number) => {
  const num = parseFloat(value);
  return isNaN(num) ? 'Invalid number' : num.toFixed(space || 2);
};

export const formatDate = (dateValue: any) => {
  return dateValue ? dayjs(dateValue).format(Date_Format) : null;
};

export const isEmailValid = (email: string): boolean => {
  const emailRx = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
  if (!email || typeof email !== 'string') return false;
  return emailRx.test(email.trim());
};

export const hasDuplicateEntry = (
  details: any[],
  targetObj: any,
  keysToCheck: string[],
  keysToIgnore?: string[]
): boolean => {
  return details?.some((existingDetail: any) =>
    areObjectsDeepEqual(existingDetail, targetObj, keysToCheck, true, keysToIgnore)
  );
};
